import { showToast } from '../utils/toast';
import { markDailyTask } from '../api/daily';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  signOut as amplifySignOut,
  getCurrentUser,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  resendSignUpCode,
  fetchUserAttributes,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Capacitor } from '@capacitor/core';
import { AppleSignIn, SignInScope } from '@capawesome/capacitor-apple-sign-in';

interface AuthUser {
  userId: string;
  username: string;
  email?: string;
  name?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  signInWithApple: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const authenticateWithAppleToken = async (identityToken: string) => {
    // Send identity token to backend to validate with Apple and get Cognito tokens
    try {
      const response = await fetch('https://saxssup4th.execute-api.us-east-1.amazonaws.com/authenticate-apple', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identityToken }),
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data?.error || data?.message || `HTTP ${response.status}`;
        throw new Error(`Apple auth failed: ${errorMsg}`);
      }
      // Backend returns Cognito tokens
      if (data.accessToken && data.idToken) {
        localStorage.setItem('cognito_access_token', data.accessToken);
        localStorage.setItem('cognito_id_token', data.idToken);
        if (data.refreshToken) {
          localStorage.setItem('cognito_refresh_token', data.refreshToken);
        }
      }
    } catch (err: any) {
      console.error('[Auth] Apple token authentication failed:', err);
      throw err;
    }
  };

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      // Get the real sub from session token (critical for federated/Apple users)
      let realUserId = currentUser.userId;
      try {
        const session = await fetchAuthSession();
        const sub = session.tokens?.accessToken?.payload?.sub;
        if (sub && typeof sub === 'string') {
          realUserId = sub;
        }
      } catch (sessionErr) {
        console.warn('[Auth] fetchAuthSession failed, using getCurrentUser userId:', sessionErr);
      }

      let attributes: Record<string, string | undefined> = {};
      try {
        attributes = await fetchUserAttributes();
      } catch (attrErr) {
        console.warn('[Auth] fetchUserAttributes failed (federated user?):', attrErr);
      }
      // Clear cached data only when switching to a DIFFERENT account
      const cachedUserId = localStorage.getItem('zeal_user_id');
      if (cachedUserId && cachedUserId !== realUserId) {
        Object.keys(localStorage).filter(k => k.startsWith('zeal_') && k !== 'zeal_user_id').forEach(k => localStorage.removeItem(k));
        sessionStorage.clear();
      }
      localStorage.setItem('zeal_user_id', realUserId);
      // For federated users, fetchUserAttributes may fail — fall back to cached display name
      let displayName = attributes.name || localStorage.getItem('zeal_display_name') || undefined;
      if (displayName) {
        localStorage.setItem('zeal_display_name', displayName);
      }
      setUser({
        userId: realUserId,
        username: currentUser.username,
        email: attributes.email || undefined,
        name: displayName,
      });
      // If no display name yet, fetch from public profile
      if (!displayName && realUserId) {
        try {
          const res = await fetch(`https://saxssup4th.execute-api.us-east-1.amazonaws.com/public-profile?user_id=${encodeURIComponent(realUserId)}`);
          if (res.ok) {
            const data = await res.json();
            const profileName = data?.profile?.name;
            if (profileName && profileName !== 'Seeker') {
              displayName = profileName;
              localStorage.setItem('zeal_display_name', profileName);
              setUser(prev => prev ? { ...prev, name: profileName } : prev);
            }
          }
        } catch {}
      }
    } catch (err) {
      // If Amplify auth fails, check for manually stored tokens (iOS native OAuth)
      const manualIdToken = localStorage.getItem('cognito_id_token');
      if (manualIdToken) {
        try {
          const payload = JSON.parse(atob(manualIdToken.split('.')[1]));
          const displayName = localStorage.getItem('zeal_display_name') || payload.name || payload.email || 'Seeker';
          setUser({
            userId: payload.sub,
            username: payload.email || payload['cognito:username'],
            email: payload.email,
            name: displayName,
          });
          localStorage.setItem('zeal_user_id', payload.sub);
        } catch (parseErr) {
          console.error('[Auth] Failed to parse manual ID token:', parseErr);
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect' || payload.event === 'signedIn') {
        checkUser();
      } else if (payload.event === 'signInWithRedirect_failure') {
        console.error('[Auth] OAuth sign-in failed:', payload.data);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // A daily login belongs to the authenticated session, not to any particular
  // page. This runs after both Amplify and native Apple auth restore the user,
  // including launches that open directly on Today instead of Dashboard.
  useEffect(() => {
    if (!user?.userId) return;
    markDailyTask('login');
  }, [user?.userId]);

  const signIn = async (email: string, password: string) => {
    const result = await amplifySignIn({ username: email, password });
    if (result.isSignedIn) {
      await checkUser();
    } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
      const err: any = new Error('User is not confirmed');
      err.name = 'UserNotConfirmedException';
      throw err;
    } else if (!result.isSignedIn) {
      throw new Error('Sign in incomplete. Please try again.');
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    await amplifySignUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          name,
        },
      },
    });
  };

  const confirmSignUp = async (email: string, code: string) => {
    await amplifyConfirmSignUp({ username: email, confirmationCode: code });
  };

  const signOut = async () => {
    await amplifySignOut();
    // Clear all zeal_ cached data so next login starts fresh
    Object.keys(localStorage).filter(k => k.startsWith('zeal_')).forEach(k => localStorage.removeItem(k));
    // Clear manual Cognito tokens (iOS native OAuth)
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_refresh_token');
    localStorage.removeItem('apple_identity_token');
    localStorage.removeItem('apple_email');
    sessionStorage.clear();
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    await amplifyResetPassword({ username: email });
  };

  const confirmResetPassword = async (email: string, code: string, newPassword: string) => {
    await amplifyConfirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  };

  const resendCode = async (email: string) => {
    await resendSignUpCode({ username: email });
  };

  const signInWithApple = async () => {
    const platform = Capacitor.getPlatform();
    
    if (platform === 'ios') {
      // Native iOS: Use @capawesome Apple Sign-In plugin (confirmed working with Capacitor 8)
      try {
        console.log('[Auth] Attempting native iOS Apple Sign-In...');
        const result = await AppleSignIn.signIn({
          scopes: [SignInScope.Email, SignInScope.FullName],
        });
        
        if (result?.idToken) {
          console.log('[Auth] Got Apple ID token, authenticating with backend...');
          // Store Apple data temporarily
          localStorage.setItem('apple_identity_token', result.idToken);
          if (result.email) {
            localStorage.setItem('apple_email', result.email);
          }
          if (result.givenName || result.familyName) {
            const fullName = `${result.givenName || ''} ${result.familyName || ''}`.trim();
            localStorage.setItem('zeal_display_name', fullName);
          }
          
          // Authenticate with backend
          await authenticateWithAppleToken(result.idToken);
          
          // Check user and set auth state
          await checkUser();
          return;
        } else {
          throw new Error('No ID token returned from Apple Sign-In');
        }
      } catch (err: any) {
        console.error('[Auth] Native Apple Sign-In failed:', err);
        // On iOS, do NOT fall back to web redirect - it will always fail
        // Instead, show the user a meaningful error
        const errorMsg = err?.message || 'Apple Sign-In failed. Please try again.';
        showToast(errorMsg, 'error');
        throw new Error(`Apple Sign-In failed: ${errorMsg}`);
      }
    } else {
      // Web only: Use Amplify's signInWithRedirect
      try {
        console.log('[Auth] Using Amplify web-based Apple Sign-In...');
        await signInWithRedirect({ provider: 'Apple' });
      } catch (err: any) {
        // If already signed in, sign out first then retry
        if (err.message?.includes('already') || err.name === 'UserAlreadyAuthenticatedException') {
          console.log('[Auth] User already authenticated, clearing and retrying...');
          await amplifySignOut({ global: false });
          setUser(null);
          await signInWithRedirect({ provider: 'Apple' });
          return;
        }
        throw err;
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        signUp,
        confirmSignUp,
        signOut,
        resetPassword,
        confirmResetPassword,
        resendCode,
        signInWithApple,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
